/**
 * Idle-time ArcGIS SDK warmup — entry-side scheduler.
 *
 * The arcgis-core chunk is ~14 MB raw / ~3.9 MB gzip and is deliberately
 * excluded from both the eager entry graph and the service-worker precache,
 * so the first visit to a map-bearing route pays:
 *   1. chunk download + parse + execute, then
 *   2. the vector-basemap handshake — style JSON → tile metadata → sprites —
 *      as serial round trips, then
 *   3. the actual viewport tiles.
 *
 * Called from the authenticated shell (MainLayout), this warms all three in
 * the background while the user is on non-map pages, so the first Map visit
 * paints tiles almost immediately. Best-effort: failures are swallowed
 * because the map page performs the same work itself on visit.
 *
 * Skipped on constrained links (saveData / 2g) — field bandwidth is precious.
 *
 * CHUNK-GRAPH DISCIPLINE — this module lives in the eager entry chunk, so it
 * must not reference @arcgis/core AT ALL, not even dynamically: a dynamic
 * import from the entry straight into the arcgis-core manualChunk makes
 * Rollup restructure the chunk graph (observed: a STATIC arcgis-core import
 * + modulepreload landed in the entry, putting the whole SDK on first
 * paint). All SDK work lives in arcgis-warmup-impl.ts, reached only through
 * the single dynamic import below — the same shape as a lazy map route.
 */

let warmupStarted = false;

export function warmArcgisSdkOnIdle(): void {
  if (warmupStarted || typeof window === 'undefined') return;
  warmupStarted = true;

  const conn = (navigator as unknown as { connection?: { saveData?: boolean; effectiveType?: string } }).connection;
  if (conn?.saveData || conn?.effectiveType === '2g' || conn?.effectiveType === 'slow-2g') return;

  // Idle-schedule with a generous timeout so the warmup never competes with
  // the dashboard's first paint but still runs on busy sessions.
  // requestIdleCallback is missing on Safari — fall back to a timer.
  const idle = window.requestIdleCallback?.bind(window);
  if (idle) {
    idle(() => { void warmNow(); }, { timeout: 15000 });
  } else {
    window.setTimeout(() => { void warmNow(); }, 3000);
  }
}

async function warmNow(): Promise<void> {
  try {
    // Importing the impl module downloads + executes the arcgis-core chunk
    // (warmup step 1); the impl then warms the basemap + tiles (steps 2-3).
    const { warmBasemapAndTiles } = await import('@/utils/arcgis-warmup-impl');
    await warmBasemapAndTiles();
  } catch {
    // Best-effort — the map page warms everything itself on visit.
  }
}
